import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import { guardarContratoEnNasPreferido, rutaCarpetaDesdeNumero } from "../../utils/abrirRutaWindows";
import { formatEuroConSimbolo, parseEuroAmount } from "../../utils/moneyParse";

const QUIEN_RECONOCE = [
  { value: "vendedor", label: "Vendedor" },
  { value: "comprador", label: "Comprador" },
];

const CONTRATO_INICIAL = [
  { value: "autorizacion", label: "Autorización venta" },
  { value: "arras", label: "Contrato arras" },
];

export default function ModalReconocimientoHonorarios({
  isOpen,
  onClose,
  onGenerado,
  expedienteId,
  empresa = "Vitalcasa",
  getDatosPantalla = null,
  importeHonorariosDefault = "",
  carpetaNasFallback = "",
  numeroExpediente = "",
  puedeModificar = true,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#f1f5f9" : c.textPrimary;

  const [quienReconoce, setQuienReconoce] = useState("vendedor");
  const [contratoInicial, setContratoInicial] = useState("autorizacion");
  const [importe, setImporte] = useState("");
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setQuienReconoce("vendedor");
    setContratoInicial("autorizacion");
    setImporte(importeHonorariosDefault || "");
    setError("");
  }, [isOpen, importeHonorariosDefault]);

  if (!isOpen) return null;

  const inp = {
    padding: "7px 10px", borderRadius: 6, fontSize: 12, width: "100%",
    background: isDark ? "#0d1117" : "#ffffff",
    border: `1px solid ${isDark ? "#4a6080" : c.inputBorder}`,
    color: tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  };
  const lbl = { fontSize: 10, fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569", marginBottom: 4, display: "block" };
  const btn = { padding: "8px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "none", fontFamily: "inherit" };

  const generar = async () => {
    if (!puedeModificar) return;
    if (!expedienteId) {
      setError("Guarda el expediente antes de generar el documento.");
      return;
    }
    const imp = parseEuroAmount(importe);
    if (imp == null || imp <= 0) {
      setError("Indica un importe válido.");
      return;
    }

    setGenerando(true);
    setError("");
    try {
      const pantalla = typeof getDatosPantalla === "function" ? getDatosPantalla() : {};
      const r = await api.post(
        `/expedientes/${expedienteId}/generar-reconocimiento-honorarios`,
        {
          quien_reconoce: quienReconoce,
          contrato_inicial: contratoInicial,
          importe: imp,
          ...(Object.keys(pantalla).length ? pantalla : {}),
        },
        { timeout: 120000 },
      );

      const data = r.data?.data || {};
      const nombre = data.nombreArchivo || "Reconocimiento honorarios.docx";
      let carpetaNas = data.carpetaNas || data.carpetaNasActualizada || carpetaNasFallback || "";
      if (!carpetaNas && numeroExpediente) carpetaNas = rutaCarpetaDesdeNumero(numeroExpediente);

      let guardadoFinal = !!data.guardadoNas;
      let rutaNas = data.rutaNas || "";
      let errorNas = data.errorNas || "";
      let needsReinstall = false;

      if (!guardadoFinal && data.archivoBase64) {
        const local = await guardarContratoEnNasPreferido({
          carpeta: carpetaNas,
          nombre,
          base64: data.archivoBase64,
        });
        guardadoFinal = local.guardadoNas;
        rutaNas = local.rutaNas || rutaNas;
        if (!guardadoFinal) errorNas = local.errorNas || errorNas;
        needsReinstall = local.needsReinstall;
      }

      onGenerado?.({
        nombre,
        rutaNas,
        carpetaNas,
        guardadoNas: guardadoFinal,
        errorNas,
        needsReinstall,
        carpetaNasActualizada: data.carpetaNasActualizada || null,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error al generar el documento");
    } finally {
      setGenerando(false);
    }
  };

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !generando) onClose?.(); }}
    >
      <div style={{
        background: isDark ? "#141820" : "#f8f9fb",
        border: `1px solid ${isDark ? "#3d4a5c" : c.cardBorder}`,
        borderRadius: 12, width: "100%", maxWidth: 420,
        boxShadow: "0 12px 40px rgba(0,0,0,0.35)", color: tx,
      }}>
        <div style={{
          background: isDark ? "linear-gradient(135deg, #1e3a5f 0%, #1a2744 100%)" : "linear-gradient(135deg, #1a3a5c 0%, #0f2540 100%)",
          padding: "14px 18px", borderRadius: "12px 12px 0 0", color: "#fff",
        }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>Reconocimiento honorarios</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>{empresa} · Word</div>
        </div>
        <div style={{ padding: "16px 18px" }}>
          <label style={lbl}>¿Quién reconoce honorarios?</label>
          <select style={{ ...inp, marginBottom: 12 }} value={quienReconoce} onChange={(e) => setQuienReconoce(e.target.value)} disabled={generando}>
            {QUIEN_RECONOCE.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <label style={lbl}>¿Contrato inicial?</label>
          <select style={{ ...inp, marginBottom: 12 }} value={contratoInicial} onChange={(e) => setContratoInicial(e.target.value)} disabled={generando}>
            {CONTRATO_INICIAL.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <label style={lbl}>Importe (€)</label>
          <input
            style={{ ...inp, marginBottom: 12 }}
            value={importe}
            onChange={(e) => setImporte(e.target.value)}
            onBlur={(e) => { const v = parseEuroAmount(e.target.value); if (v != null) setImporte(formatEuroConSimbolo(v)); }}
            placeholder="Honorarios totales"
            disabled={generando}
          />

          {error && <div style={{ fontSize: 11, color: "#ef4444", marginBottom: 10 }}>{error}</div>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" style={{ ...btn, background: isDark ? "#334155" : "#e2e8f0", color: tx }} onClick={onClose} disabled={generando}>
              Cancelar
            </button>
            <button
              type="button"
              style={{ ...btn, background: "#2563eb", color: "#fff" }}
              onClick={generar}
              disabled={generando || !puedeModificar}
              title={!puedeModificar ? "No tienes permiso para generar este documento" : undefined}
            >
              {generando ? "Generando…" : "Generar Word"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
